"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";

import { CATALOG_SEARCH_MAX_LENGTH } from "@/lib/host-panel/catalog-query";

/**
 * The catalog's search, the first line of the filter block (handoff v2 §6.2, filters spec §3).
 *
 * **Typing does not filter.** Only the submit applies — Enter in the field or a press on the
 * magnifier. The advisory line under it is always there and always as tall, so applying a term
 * never moves the grid; it shows only while a typed term waits to be applied, or when the
 * applied one found nothing (v2's `qAvisoOp`).
 */

const NOTICE = {
  pending: "Toque na lupa para aplicar o que você digitou.",
  empty: "Nenhuma peça para essa busca. Revise o que digitou e busque de novo.",
  applied: "Busca aplicada.",
} as const;

/** Matches by the piece's name or its collection (§3) — what the host, the guest and the preview read. */
export const CATALOG_SEARCH_PLACEHOLDER = "Busque pelo nome da peça ou da coleção";

export type CatalogSearchFieldProps = {
  /** The term already applied — what the grid is showing. */
  applied: string;
  onApply: (term: string) => void;
  /** How many pieces the applied term found, for the "no result" advice. */
  resultCount: number;
  /**
   * The words in the empty field. Default: the host's (`CATALOG_SEARCH_PLACEHOLDER`). The
   * internal area says SKU instead of collection (§6.2) — the staff look pieces up by code, and
   * the served catalogue does match the SKU (`CATALOG_PUBLIC_TEXT_SEARCH_FIELDS` in the API).
   */
  placeholder?: string;
};

export function CatalogSearchField({
  applied,
  onApply,
  resultCount,
  placeholder = CATALOG_SEARCH_PLACEHOLDER,
}: CatalogSearchFieldProps) {
  const [typed, setTyped] = useState(applied);
  const lastApplied = useRef(applied);

  /**
   * "Limpar filtros" drops the applied term from outside this field. Without this the box would
   * keep the old word, the × would keep offering to clear what is already clear, and the advice
   * would ask forever for a search that is not pending.
   */
  useEffect(() => {
    if (applied === lastApplied.current) return;
    lastApplied.current = applied;
    setTyped(applied);
  }, [applied]);

  function submit(event: FormEvent) {
    event.preventDefault();
    onApply(typed.trim());
  }

  function clear() {
    // The × zeroes the typed term **and** the applied one, and goes back to page 1 (§3).
    setTyped("");
    onApply("");
  }

  const isPending = typed.trim() !== applied;
  const isEmpty = applied !== "" && resultCount === 0;
  const notice = isPending ? NOTICE.pending : isEmpty ? NOTICE.empty : NOTICE.applied;

  return (
    <form
      role="search"
      onSubmit={submit}
      // Centred between the block's rules: the line's own padding, 12px more above than below.
      className="flex flex-col gap-[7px] border-b border-[var(--tb-hairline)] pb-px pt-[25px] [@media(min-width:1024px)]:pb-0.5 [@media(min-width:1024px)]:pt-[26px]"
    >
      <span className="flex w-full min-w-0 items-stretch border border-[var(--tb-border)] bg-tb-white transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:border-[var(--fg)] focus-within:border-[var(--fg)] [@media(min-width:1024px)]:max-w-[420px]">
        <input
          type="search"
          value={typed}
          maxLength={CATALOG_SEARCH_MAX_LENGTH}
          onChange={(event) => setTyped(event.target.value)}
          aria-label="Buscar no catálogo"
          placeholder={placeholder}
          className="min-h-[42px] min-w-0 flex-auto appearance-none bg-transparent py-2.5 pl-3.5 pr-1 font-sans text-[14px] leading-[normal] text-[var(--fg)] outline-none [&::-webkit-search-cancel-button]:hidden"
        />

        {typed === "" ? null : (
          <button
            type="button"
            onClick={clear}
            aria-label="Limpar busca"
            className="grid w-8 flex-none cursor-pointer place-content-center text-[var(--fg-subtle)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:text-[var(--fg)]"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}

        {/* The magnifier is the submit; a fine rule sets it apart from the field. */}
        <button
          type="submit"
          aria-label="Buscar no catálogo"
          className="grid w-[46px] flex-none cursor-pointer place-content-center border-l border-[var(--tb-border)] text-[var(--fg)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-porcelain-tint)]"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
        </button>
      </span>

      {/* Always present, 17px: the grid does not move when the advice changes. Filters handoff §3
          says 17px and so does the prototype V2 (`03-prototipo-catalogo.template.html:2523`,
          `min-height:17px`). It was moved to 18px on the strength of a "§6.2" that neither of
          those documents carries; the two that can be checked agree on 17. */}
      <span
        aria-live="polite"
        className="min-h-[17px] text-[12.5px] leading-[1.4] text-[var(--fg-muted)] transition-opacity duration-[var(--tb-duration-2)]"
        style={{ opacity: isPending || isEmpty ? 1 : 0 }}
      >
        {notice}
      </span>
    </form>
  );
}
